/**
 * DATValuateExpert.jsx — SilverDAT valuateExpert in der Bewertungs-Step
 * ---------------------------------------------------------------------
 * Embeds SilverDAT valuateExpert (Bewertungs-Modul) in den Bewertungs-
 * Step. Im Unterschied zu calculateExpert läuft hier:
 *   1. Backend POST /api/cases/{id}/dat/valuate/create-dossier
 *      → DAT SOAP Dossier1N.createDossierN
 *      → DAT antwortet mit `dossier_id` und bereits gefüllter Bewertung
 *        (WBW, Restwert, Neupreis).
 *   2. Backend GET /api/cases/{id}/dat/valuate/iframe-url
 *      → Liefert eine signierte SSO-URL (Token in Query) + Deep-Links
 *        ins Dossier.
 *   3. Wir embedden die URL direkt im Iframe (DAT setzt SSO-Cookie auf
 *      *.dat.de und springt direkt in den Bewertungs-Overview).
 *   4. Optional: Button „Daten aus DAT zurückholen" pullt aktualisierte
 *      Bewertungs-Werte (WBW, Restwert, etc.) zurück in die Akte.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Loader2, Scale, RefreshCcw, AlertTriangle, Maximize2,
  CheckCircle2, ExternalLink, Download,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmtEur = (v) => (Number(v || 0)).toLocaleString("de-DE", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2,
});

export default function DATValuateExpert({ caseId, vehicle, onValuationChanged }) {
  const [loading, setLoading] = useState(false);
  const [iframeDoc, setIframeDoc] = useState(null);  // srcDoc HTML with SSO form
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dossierInfo, setDossierInfo] = useState(null);  // {contract_id, contract_url, deep_links, valuation}
  const [pulling, setPulling] = useState(false);
  const [activeStep, setActiveStep] = useState("overview");
  const iframeRef = useRef(null);
  const targetUrlRef = useRef(null);
  const navigatedRef = useRef(false);

  const escapeHTML = (s) => String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  // Auf Mount: prüfen ob Akte bereits ein valuateExpert-Dossier hat
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: caseDoc } = await api.get(`/cases/${caseId}`);
        if (cancelled) return;
        const c = caseDoc?.case || {};
        const valId = c.dat_valuation_contract_id;
        if (valId) {
          setDossierInfo({
            contract_id: valId,
            valuation: c.valuation?.dat_valuation || {},
          });
        }
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  /**
   * Full launch flow analog zu DATCalculateExpert:
   *  1. Dossier in DAT anlegen (oder existing wiederverwenden)
   *  2. Deep-Links holen
   *  3. SSO-Form bauen (POST mit Session-Token) → DAT setzt JSESSIONID-Cookie
   *  4. Nach Iframe-Load → navigate iframe.src auf deep_links.overview
   */
  const launch = useCallback(async () => {
    setLoading(true);
    setError(null);
    navigatedRef.current = false;
    try {
      // Step 1: Dossier anlegen / wiederverwenden
      const { data: dossier } = await api.post(`/cases/${caseId}/dat/valuate/create-dossier`);
      const val = dossier.valuation || {};
      if (dossier.reused) {
        toast.message(`Bestehendes DAT-Bewertungs-Dossier #${dossier.contract_id} — Iframe wird geöffnet.`);
      } else {
        const wbw = val.replacement_value ? ` · WBW ${fmtEur(val.replacement_value)}` : "";
        toast.success(`DAT-Bewertungs-Dossier #${dossier.contract_id} angelegt${wbw}.`);
      }

      // Step 2: Deep-Links holen
      const { data: ssoData } = await api.get(`/cases/${caseId}/dat/valuate/iframe-url`);
      setDossierInfo({
        contract_id: dossier.contract_id,
        contract_url: dossier.contract_url || ssoData.deep_links?.overview,
        deep_links: ssoData.deep_links,
        valuation: val,
      });
      targetUrlRef.current = ssoData.deep_links?.overview;
      onValuationChanged?.(val);

      // Step 3: SSO-Launch für valuateNG.expert
      const { data: sso } = await api.post(`/dat/sso-launch?product_variant=valuateNG.expert`);

      // Step 4: Auto-Submit-Form bauen
      const e = escapeHTML;
      const html = `<!doctype html><html lang="de"><head>
<meta charset="utf-8"><title>DAT wird geladen…</title>
<style>html,body{margin:0;padding:0;height:100%;background:#fafafa;font-family:system-ui,-apple-system,sans-serif;color:#52525b}
.wrap{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:14px}
.spinner{width:38px;height:38px;border:3px solid #e4e4e7;border-top-color:#06b6d4;border-radius:50%;animation:spin 0.7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
small{color:#a1a1aa;font-size:11px;letter-spacing:0.1em;text-transform:uppercase}</style></head>
<body>
<div class="wrap"><div class="spinner"></div><div>SilverDAT valuateExpert wird gestartet…</div>
<small>Auto-Login als ${e(sso.login)} · Dossier #${e(dossier.contract_id)}</small></div>
<form id="ssoform" method="POST" action="${e(sso.url)}" style="display:none">
  <input type="hidden" name="${e(sso.transmission_key)}" value="${e(sso.token)}">
  <input type="hidden" name="customerNumber" value="${e(sso.customer_number)}">
  <input type="hidden" name="login" value="${e(sso.login)}">
</form>
<script>document.getElementById('ssoform').submit();</script>
</body></html>`;
      setIframeDoc(html);
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [caseId, onValuationChanged]);

  /**
   * After SSO form has POSTed and DAT set JSESSIONID, navigate iframe
   * directly to the dossier overview.
   */
  const handleIframeLoad = useCallback(() => {
    if (!targetUrlRef.current || navigatedRef.current) return;
    if (!iframeRef.current) return;
    navigatedRef.current = true;
    setTimeout(() => {
      try {
        iframeRef.current.src = targetUrlRef.current;
      } catch {
        /* ignored — cross-origin */
      }
    }, 600);
  }, []);

  /**
   * Pull-Back: Aktualisierte Bewertungs-Werte aus DAT in die Akte
   * zurückholen — z.B. nach Eingabe von Wertminderungs-Faktoren im Iframe.
   */
  const pullBack = useCallback(async () => {
    if (!dossierInfo?.contract_id) return;
    setPulling(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/dat/valuate/pull-back`);
      const val = data.valuation || {};
      const parts = [];
      if (val.replacement_value) parts.push(`WBW ${fmtEur(val.replacement_value)}`);
      if (val.restwert) parts.push(`Restwert ${fmtEur(val.restwert)}`);
      if (val.original_price) parts.push(`Neupreis ${fmtEur(val.original_price)}`);
      if (parts.length === 0) {
        toast.info("Dossier enthält noch keine Bewertungs-Werte. Bitte erst in DAT bewerten.");
      } else {
        toast.success(`Bewertung aktualisiert: ${parts.join(" · ")}`);
      }
      setDossierInfo((d) => ({ ...d, valuation: val }));
      onValuationChanged?.(val);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPulling(false);
    }
  }, [caseId, dossierInfo?.contract_id, onValuationChanged]);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden mb-4" data-testid="dat-valuate-expert-block">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3 bg-gradient-to-r from-cyan-50/50 to-white">
        <div className="h-9 w-9 rounded-lg brand-gradient-bg flex items-center justify-center shrink-0">
          <Scale className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">SilverDAT valuateExpert</div>
          <div className="font-display font-semibold text-base">Fahrzeugbewertung in DAT</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Wir legen automatisch ein Bewertungs-Dossier mit VIN, KBA und Laufleistung an —
            DAT liefert WBW, Restwert und Neupreis direkt zurück.
          </div>
        </div>
        <div className="flex gap-2">
          {iframeDoc && (
            <>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => setExpanded((v) => !v)}
                data-testid="dat-val-expand-btn"
                title={expanded ? "Standardgröße" : "Vollbild"}
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
                {expanded ? "Verkleinern" : "Vollbild"}
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={launch} disabled={loading}
                data-testid="dat-val-reload-btn"
                title="Iframe-Session neu laden"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              </Button>
            </>
          )}
          {!iframeDoc && (
            <Button
              type="button"
              onClick={launch}
              disabled={loading}
              className="brand-gradient-bg text-white"
              data-testid="dat-val-open-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Scale className="h-3.5 w-3.5 mr-2" />}
              Bewertung in DAT anlegen + öffnen
            </Button>
          )}
        </div>
      </div>

      {/* Dossier-Status mit Bewertungs-Werten */}
      {dossierInfo?.contract_id && (
        <>
          <div className="px-5 py-2.5 bg-emerald-50/70 border-b border-emerald-200 flex items-center gap-3 flex-wrap text-xs">
            <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
            <span className="font-semibold text-emerald-900">
              DAT-Bewertungs-Dossier #{dossierInfo.contract_id}
            </span>
            {dossierInfo.contract_url && (
              <a
                href={dossierInfo.contract_url}
                target="_blank" rel="noreferrer"
                className="text-emerald-700 underline inline-flex items-center gap-1 hover:text-emerald-900"
                data-testid="dat-val-external-link"
              >
                <ExternalLink className="h-3 w-3" />
                In neuem Tab öffnen
              </a>
            )}
            <Button
              type="button" size="sm" variant="outline"
              onClick={pullBack} disabled={pulling}
              className="ml-auto h-7 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-100"
              data-testid="dat-val-pull-back-btn"
            >
              {pulling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
              Bewertung aus DAT zurückholen
            </Button>
          </div>

          {/* Aktuelle Bewertungswerte aus DAT */}
          {dossierInfo.valuation && Object.keys(dossierInfo.valuation).length > 0 && (
            <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/40 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {dossierInfo.valuation.replacement_value && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">WBW (brutto)</div>
                  <div className="font-bold text-zinc-900 mt-0.5">{fmtEur(dossierInfo.valuation.replacement_value)}</div>
                </div>
              )}
              {dossierInfo.valuation.restwert && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Restwert</div>
                  <div className="font-bold text-zinc-900 mt-0.5">{fmtEur(dossierInfo.valuation.restwert)}</div>
                </div>
              )}
              {dossierInfo.valuation.original_price && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Neupreis</div>
                  <div className="font-bold text-zinc-900 mt-0.5">{fmtEur(dossierInfo.valuation.original_price)}</div>
                </div>
              )}
              {dossierInfo.valuation.purchase_price && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Ankaufspreis</div>
                  <div className="font-bold text-zinc-900 mt-0.5">{fmtEur(dossierInfo.valuation.purchase_price)}</div>
                </div>
              )}
            </div>
          )}

          {/* Deep-Link Step-Switcher */}
          {iframeDoc && dossierInfo.deep_links && (
            <DATValuateStepSwitcher
              deepLinks={dossierInfo.deep_links}
              active={activeStep}
              onSwitch={(stepKey) => {
                const url = dossierInfo.deep_links[stepKey];
                if (!url || !iframeRef.current) return;
                setActiveStep(stepKey);
                navigatedRef.current = true;
                iframeRef.current.src = url;
              }}
            />
          )}
        </>
      )}

      {/* Vehicle context */}
      {!iframeDoc && (vehicle?.vin || vehicle?.license_plate) && (
        <div className="px-5 py-2.5 bg-zinc-50/50 border-b border-zinc-100 text-xs text-zinc-600 flex flex-wrap gap-x-5 gap-y-1">
          {vehicle?.license_plate && <span><strong className="text-zinc-800">Kennzeichen:</strong> {vehicle.license_plate}</span>}
          {vehicle?.vin && <span><strong className="text-zinc-800">VIN:</strong> {vehicle.vin}</span>}
          {(vehicle?.make || vehicle?.model) && <span><strong className="text-zinc-800">Fahrzeug:</strong> {vehicle.make} {vehicle.model}</span>}
          {vehicle?.mileage && <span><strong className="text-zinc-800">KM-Stand:</strong> {vehicle.mileage.toLocaleString("de-DE")} km</span>}
        </div>
      )}

      {/* Iframe / Placeholder / Error */}
      <div className="bg-white">
        {error && (
          <div className="p-6 text-sm text-rose-700 bg-rose-50 border-t border-rose-200 flex items-start gap-3" data-testid="dat-val-error">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>
              <div className="font-semibold">DAT-Bewertungs-Anbindung fehlgeschlagen</div>
              <div className="mt-0.5">{error}</div>
              <div className="mt-2 text-xs text-rose-600">
                Häufige Ursachen: VIN fehlt, KM-Stand nicht erfasst, DAT-Vertrag enthält
                kein valuateExpert. Bitte unter <strong>Einstellungen → API-Zugänge</strong>
                Kundennummer, Benutzer und Signatur prüfen.
              </div>
            </div>
          </div>
        )}

        {!iframeDoc && !error && !dossierInfo?.contract_id && (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="dat-val-empty">
            Klicke auf <strong>„Bewertung in DAT anlegen + öffnen"</strong>.
            <div className="text-xs text-zinc-400 mt-1">
              Wir legen das Bewertungs-Dossier in DAT an und springen direkt ins
              valuateExpert-Modul — Wiederbeschaffungswert, Restwert und Neupreis
              werden automatisch in diese Akte übernommen.
            </div>
          </div>
        )}

        {iframeDoc && (
          <iframe
            ref={iframeRef}
            srcDoc={iframeDoc}
            onLoad={handleIframeLoad}
            title="DAT valuateExpert"
            className="w-full block"
            style={{ height: expanded ? "85vh" : "780px", border: 0 }}
            allow="clipboard-read; clipboard-write; camera; microphone"
            data-testid="dat-val-iframe"
          />
        )}
      </div>
    </div>
  );
}


/**
 * DATValuateStepSwitcher — Tab-Leiste oberhalb des valuateExpert-Iframes.
 * Steps gemäß DAT-Workflow: Overview · Fahrzeug · Bewertung · Drucken.
 */
function DATValuateStepSwitcher({ deepLinks, active, onSwitch }) {
  const steps = [
    { key: "overview", label: "Übersicht", testid: "dat-val-step-overview" },
    { key: "vehicle", label: "Fahrzeug", testid: "dat-val-step-vehicle" },
    { key: "valuation", label: "Bewertung", testid: "dat-val-step-valuation" },
    { key: "print", label: "Drucken", testid: "dat-val-step-print" },
  ];
  return (
    <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex flex-wrap gap-1" data-testid="dat-val-step-switcher">
      {steps.map((s) => {
        const isActive = active === s.key;
        const enabled = !!deepLinks[s.key];
        return (
          <button
            key={s.key}
            type="button"
            disabled={!enabled}
            onClick={() => enabled && onSwitch(s.key)}
            data-testid={s.testid}
            className={[
              "px-3 py-1 text-[11px] rounded-md font-semibold transition-colors",
              isActive
                ? "brand-gradient-bg text-white shadow"
                : enabled
                ? "bg-white border border-zinc-200 text-zinc-700 hover:border-cyan-300 hover:text-cyan-700"
                : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
            ].join(" ")}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
